import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useEmployeeSessionContext } from "@/contexts/employeeSessionContext";
import {
  fetchLinkedEmployeeCached,
  invalidateLinkedEmployee,
  peekLinkedEmployee,
} from "@/lib/network/employeeDataCache";
import { friendlyNetworkMessage } from "@/lib/network/networkMessages";
import type { EmployeeRow } from "@/types/database";

export type LinkedEmployeeState = {
  employeeId: string | null;
  linkedEmployee: EmployeeRow | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
};

const IDLE_LINKED: LinkedEmployeeState = {
  employeeId: null,
  linkedEmployee: null,
  loading: false,
  error: null,
  retry: () => {},
};

/** Employee row when the signed-in user is linked to `employees.user_id` (same org). */
export function useLinkedEmployee(): LinkedEmployeeState {
  const session = useEmployeeSessionContext();
  const local = useLinkedEmployeeState({ disabled: Boolean(session) });
  if (session) return session.linked;
  return local;
}

/** Internal hook — use {@link useLinkedEmployee} or {@link EmployeeSessionProvider}. */
export function useLinkedEmployeeState(options?: { disabled?: boolean }): LinkedEmployeeState {
  const disabled = options?.disabled ?? false;
  const { user, isInitialized } = useAuth();
  const orgId = user?.org_id ?? null;
  const userId = user?.id ?? null;

  const cached =
    orgId && userId ? peekLinkedEmployee(orgId, userId) : undefined;
  const hasCached = cached !== undefined;

  const [linkedEmployee, setLinkedEmployee] = useState<EmployeeRow | null>(
    hasCached ? cached : null,
  );
  const [loading, setLoading] = useState(!isInitialized || (!hasCached && Boolean(orgId && userId)));
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (force = false) => {
      if (!isInitialized) {
        setLoading(true);
        return;
      }

      if (!orgId || !userId) {
        setLinkedEmployee(null);
        setLoading(false);
        setError(null);
        return;
      }

      const stale = peekLinkedEmployee(orgId, userId);
      if (stale !== undefined && !force) {
        setLinkedEmployee(stale);
        setLoading(false);
        return;
      }

      if (stale === undefined) setLoading(true);
      setError(null);

      try {
        const row = await fetchLinkedEmployeeCached(orgId, userId, { force });
        setLinkedEmployee(row);
      } catch (e) {
        setError(friendlyNetworkMessage(e, "Could not load your profile. Try again."));
        /* Keep last known row on soft failure. */
      } finally {
        setLoading(false);
      }
    },
    [isInitialized, orgId, userId],
  );

  useEffect(() => {
    if (disabled) return;
    if (!isInitialized) {
      setLoading(true);
      return;
    }
    void load(false);
  }, [disabled, load, isInitialized, user?.user_type, user?.role_type]);

  const retry = useCallback(() => {
    if (orgId && userId) invalidateLinkedEmployee(orgId, userId);
    void load(true);
  }, [load, orgId, userId]);

  if (disabled) return IDLE_LINKED;

  return {
    employeeId: linkedEmployee?.id ?? null,
    linkedEmployee,
    loading,
    error,
    retry,
  };
}
